export const MAX_ROWS = 500;
export const MAX_COLUMNS = 30;
export const MAX_FILE_BYTES = 2 * 1024 * 1024;
export const FONTS = { Verdana: 'Verdana, Geneva, sans-serif', Arial: 'Arial, sans-serif', Georgia: 'Georgia, serif' };
const EMAIL = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;
const MERGE = /<<([^<>]+)>>/g;

/** Enforces one plain email address per row, unique headers and bounded cell values. */
export function validateTable(matrix) {
    const populated = matrix.filter(row => row.some(value => String(value ?? '').trim()));
    if (populated.length < 2) throw new Error('Include a header row and at least one recipient.');
    const headers = populated[0].map(value => String(value ?? '').trim());
    if (headers[0] !== 'Email') throw new Error('The first column must be named Email.');
    if (headers.length > MAX_COLUMNS || headers.some(name => !name || name.length > 64 || /[<>\x00-\x1f]/.test(name))) throw new Error('Use 1–30 named columns with no blank headers or angle brackets.');
    if (new Set(headers.map(name => name.toLowerCase())).size !== headers.length) throw new Error('Every column name must be unique.');
    const rows = populated.slice(1).map((row, index) => {
        if (row.length > headers.length) throw new Error(`Data row ${index + 1} has more values than column headers.`);
        const cells = headers.map((_, column) => String(row[column] ?? ''));
        if (cells.some(value => value.length > 2000 || /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/.test(value))) throw new Error(`Data row ${index + 1} contains an unsupported or oversized value.`);
        cells[0] = cells[0].trim().toLowerCase();
        const local = cells[0].split('@')[0];
        if (!EMAIL.test(cells[0]) || cells[0].length > 254 || local.length > 64 || local.startsWith('.') || local.endsWith('.') || local.includes('..')) throw new Error(`Data row ${index + 1} must contain one valid Email address.`);
        return cells;
    });
    if (rows.length > MAX_ROWS) throw new Error(`Upload no more than ${MAX_ROWS} recipients per batch.`);
    if (new Set(rows.map(row => row[0])).size !== rows.length) throw new Error('Duplicate Email addresses found. Remove duplicate recipients before continuing.');
    return { headers, rows };
}

/** Return unique recipient addresses whose domains are outside the approved list. */
export function getUnapprovedRecipients(rows, approvedDomains) {
    const domains = (Array.isArray(approvedDomains) ? approvedDomains : []).map(value => String(value).trim().toLowerCase().replace(/^@/, '').replace(/^\./, '')).filter(Boolean);
    return [...new Set(rows.map(row => String(row?.[0] || '').trim().toLowerCase()).filter(address => {
        const domain = address.slice(address.lastIndexOf('@') + 1);
        return !domains.some(approved => domain === approved || domain.endsWith(`.${approved}`));
    }))];
}

/** Substitute once from the same row; inserted data is never treated as another merge field. */
export function mergeText(text, headers, row) {
    return text.replace(MERGE, (_, name) => row[headers.indexOf(name)] ?? `<<${name}>>`);
}

/** Escape every user-controlled character before generating the small allowed HTML format. */
function escapeHtml(value) {
    return value.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#x27;' }[char]));
}

/** Render only toolbar markup, with spreadsheet values escaped inside each text segment. */
export function renderBody(template, headers, row) {
    const stack = [];
    const tags = { '**': 'strong', '__': 'u', '*': 'em' };
    const parts = template.split(/(<<[^<>]+>>|\*\*|__|\*)/g).map(part => {
        // A linear token stream keeps merge values separate from formatting markers.
        if (Object.prototype.hasOwnProperty.call(tags, part)) {
            const index = stack.indexOf(part);
            if (index < 0) { stack.push(part); return `<${tags[part]}>`; }
            let closing = '';
            while (stack.length > index) closing += `</${tags[stack.pop()]}>`;
            return closing;
        }
        return escapeHtml(mergeText(part, headers, row)).replace(/\r\n|\r|\n/g, '<br>');
    });
    while (stack.length) parts.push(`</${tags[stack.pop()]}>`);
    return parts.join('');
}

/** Validate all recipients' merged subjects and required values, not only the visible preview. */
export function validateMessage(subject, body, headers, rows) {
    if (!subject.trim() || subject.length > 200 || /[\r\n]/.test(subject)) return 'Enter a subject of 1–200 characters on one line.';
    if (!body.replace(/\*\*|__|\*/g, '').trim() || body.length > 20000) return 'Enter a message of 1–20,000 characters.';
    const combined = subject + '\n' + body;
    const fields = [...combined.matchAll(MERGE)].map(match => match[1]);
    if (fields.some(name => !headers.includes(name))) return 'A merge field does not match a column name. Use Add merge field to insert it.';
    if (/<<|>>/.test(combined.replace(MERGE, ''))) return 'A merge field is incomplete. Use <<Column name>>.';
    for (let index = 0; index < rows.length; index += 1) {
        if (fields.some(name => !rows[index][headers.indexOf(name)].trim())) return `Data row ${index + 1} has an empty value for a merge field used in the message.`;
        const merged = mergeText(subject, headers, rows[index]);
        if (merged.length > 200 || /[\r\n]/.test(merged)) return `Data row ${index + 1} produces a subject longer than 200 characters or containing a line break.`;
        if (new TextEncoder().encode(renderBody(body, headers, rows[index])).length > 100000) return `Data row ${index + 1} produces a message that is too large.`;
    }
    return '';
}
